import React, { useState, useRef } from "react"
import { Loader2, Download, UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, X } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "src/components/ui/sheet"
import { Button } from "src/components/ui/button"
import { useToast } from "src/components/ui/toast"
import { importUnits, downloadUnitTemplate } from "../api/projectsApi"

// Bulk-import units from an .xlsx. Shows a template-download shortcut, a file
// picker, and per-row validation errors after an attempt.
export function ImportUnitsDrawer({ open, projectId, onClose, onSuccess }) {
  const { toast } = useToast()
  const inputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [partial, setPartial] = useState(false)
  const [pending, setPending] = useState(false)
  const [result, setResult] = useState(null) // { imported, towers_created, row_errors }

  function reset() {
    setFile(null)
    setResult(null)
    setPartial(false)
  }

  function handleClose() {
    reset()
    onClose()
  }

  function pickFile(f) {
    if (!f) return
    setFile(f)
    setResult(null)
  }

  async function handleTemplate() {
    try {
      await downloadUnitTemplate(projectId)
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Could not download the template" })
    }
  }

  async function handleImport() {
    if (!file) return
    setPending(true)
    setResult(null)
    try {
      const data = await importUnits(projectId, file, { partial })
      setResult(data)
      if (data.imported > 0) {
        toast({
          title: "Import complete",
          description: `${data.imported} unit${data.imported === 1 ? "" : "s"} imported` +
            (data.towers_created ? `, ${data.towers_created} tower${data.towers_created === 1 ? "" : "s"} created` : ""),
        })
        onSuccess?.()
      }
    } catch (err) {
      const data = err.response?.data
      setResult({ imported: 0, row_errors: data?.row_errors || [] })
      const msg = data?.errors?.[0] || "Import failed"
      toast({ variant: "destructive", title: "Import failed", description: msg })
    } finally {
      setPending(false)
    }
  }

  const rowErrors = result?.row_errors || []

  return (
    <Sheet open={open} onOpenChange={(v) => !v && handleClose()}>
      <SheetContent side="right" className="flex flex-col p-0 sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>Import units from Excel</SheetTitle>
          <SheetDescription>
            Upload an .xlsx using the Clozdl template. Towers are auto-created from the Tower column.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          {/* Template download */}
          <button
            type="button"
            onClick={handleTemplate}
            className="flex w-full items-center gap-3 rounded-lg bg-blue-50 px-3 py-2.5 text-left transition-colors hover:bg-blue-100"
          >
            <Download className="h-4 w-4 text-blue-700" />
            <span className="flex-1 text-[13px] font-medium text-blue-800">Download template (.xlsx)</span>
          </button>

          {/* File picker */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]) }}
            className="flex cursor-pointer flex-col items-center gap-1 rounded-lg border border-dashed border-slate-300 px-4 py-8 text-center transition-colors hover:border-slate-400"
          >
            <UploadCloud className="h-7 w-7 text-slate-400" />
            <span className="text-[13px] text-slate-600">
              Drop file or <span className="font-medium text-blue-700">browse</span>
            </span>
            <span className="text-[11px] text-slate-400">.xlsx up to 5,000 rows</span>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
            />
          </div>

          {file && (
            <div className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2">
              <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
              <span className="flex-1 truncate text-[13px] text-slate-700">{file.name}</span>
              <button type="button" onClick={reset} aria-label="Remove file" className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <label className="flex items-center gap-2 text-[13px] text-slate-600">
            <input type="checkbox" checked={partial} onChange={(e) => setPartial(e.target.checked)} />
            Import valid rows even if some have errors
          </label>

          {/* Result */}
          {result && (
            <div className="space-y-2">
              {result.imported > 0 && (
                <div className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-[13px] text-emerald-800">
                  <CheckCircle2 className="h-4 w-4" />
                  {result.imported} unit{result.imported === 1 ? "" : "s"} imported
                  {result.towers_created ? `, ${result.towers_created} tower${result.towers_created === 1 ? "" : "s"} created` : ""}
                </div>
              )}
              {rowErrors.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                  <p className="flex items-center gap-1.5 text-[13px] font-medium text-amber-800">
                    <AlertTriangle className="h-4 w-4" />
                    {rowErrors.length} row{rowErrors.length === 1 ? "" : "s"} could not be imported
                  </p>
                  <ul className="mt-1.5 max-h-40 space-y-1 overflow-y-auto text-[12px] text-amber-700">
                    {rowErrors.map((re) => (
                      <li key={re.row}>
                        <span className="font-medium">Row {re.row}:</span> {re.messages.join(", ")}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
          <Button type="button" variant="ghost" onClick={handleClose} disabled={pending}>
            {result?.imported > 0 ? "Done" : "Cancel"}
          </Button>
          <Button type="button" onClick={handleImport} disabled={pending || !file} className="gap-2">
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            Import units
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
